import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, MailCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Field, InlineAlert, Input } from "../../shared/ui";
import { AuthShell } from "./AuthShell";

const MIN_PASSWORD_LENGTH = 12;

const requestSchema = z.object({
  email: z.string().min(1, "Enter your email address").email("That does not look like an email"),
});

const resetSchema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters`)
      .max(128, "That is too long"),
    confirm: z.string(),
  })
  .refine((values) => values.password === values.confirm, {
    message: "The two passwords do not match",
    path: ["confirm"],
  });

/**
 * Requests a reset link.
 *
 * <p>The confirmation is deliberately vague about whether the address matched an account. The API
 * answers identically either way, and a page that said "no such user" would undo that in one line
 * — turning this form into a way to test who works here, with no credentials needed.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof requestSchema>>({ resolver: zodResolver(requestSchema) });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post("/api/v1/auth/forgot-password", { email: values.email });
    } catch {
      // Swallowed deliberately. Reporting a failure would distinguish a rejected address from an
      // accepted one, which is exactly what the endpoint avoids doing.
    }
    setSent(true);
  });

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2.5">
            <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
            <p className="text-sm text-brand-700">
              If that address belongs to an account, a reset link is on its way. It expires in an
              hour and can be used once.
            </p>
          </div>
          <Button variant="secondary" className="w-full" onClick={() => navigate("/login")}>
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="We will email you a link.">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus {...register("email")} />
        </Field>

        <Button type="submit" loading={isSubmitting} className="w-full">
          {isSubmitting ? "Sending…" : "Send reset link"}
        </Button>

        <Button variant="ghost" className="w-full" onClick={() => navigate("/login")}>
          Back to sign in
        </Button>
      </form>
    </AuthShell>
  );
}

/** Completes a reset using the emailed token. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof resetSchema>>({ resolver: zodResolver(resetSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post("/api/v1/auth/reset-password", { token, password: values.password });
      setDone(true);
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.code === "TOKEN_INVALID"
          ? "This reset link is no longer valid. Request a new one."
          : "Could not reset your password. Try requesting a new link.",
      );
    }
  });

  if (!token) {
    return (
      <AuthShell title="Reset link missing">
        <InlineAlert tone="error">Open the link from your email directly.</InlineAlert>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password changed">
        <div className="space-y-4">
          {/* Worth saying plainly: the reader may be signed in elsewhere and should know why
              those sessions have ended. */}
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <p className="text-sm text-emerald-700">
              Your password has been changed, and you have been signed out everywhere else.
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate("/login")}>
            Sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password">
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <Field
          label="New password"
          required
          error={errors.password?.message}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        >
          <Input type="password" autoComplete="new-password" autoFocus {...register("password")} />
        </Field>

        <Field label="Confirm password" required error={errors.confirm?.message}>
          <Input type="password" autoComplete="new-password" {...register("confirm")} />
        </Field>

        <Button type="submit" loading={isSubmitting} className="w-full">
          {isSubmitting ? "Changing…" : "Change password"}
        </Button>
      </form>
    </AuthShell>
  );
}
